import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { Form, Input, Select, Button, Space, Row, Col, Typography, Avatar, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { debounce } from 'lodash';
import ContactsServices from '../../Contacts/ContactsServices';
import { getChannels } from '../../Channels/ChannelsServices';

const { TextArea } = Input;
const { Text } = Typography;

const CATEGORIES = ['escalation', 'reclamation', 'suggestion', 'order', 'other'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

const CreateTicketModal = ({ onClose, onSubmit }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const { handleError } = useErrorHandler();
    const [submitting, setSubmitting] = useState(false);
    
    const selectedChannelId = Form.useWatch('channel_id', form);

    const [channels, setChannels] = useState([]);
    const [searchChannelsLoading, setSearchChannelsLoading] = useState(false);
    const [searchStringChannel, setSearchStringChannel] = useState('');
    const [channelPage, setChannelPage] = useState(1);
    const [hasMoreChannels, setHasMoreChannels] = useState(true);

    const [contacts, setContacts] = useState([]);
    const [searchContactsLoading, setSearchContactsLoading] = useState(false);
    const [searchStringContact, setSearchStringContact] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const channelsAbortControllerRef = useRef(null);
    const contactsAbortControllerRef = useRef(null);

    const fetchChannels = useCallback(async (search_string = '', currentPage = 1) => {
        if (channelsAbortControllerRef.current) {
            channelsAbortControllerRef.current.abort();
        }

        const controller = new AbortController();
        channelsAbortControllerRef.current = controller;

        try {
            setSearchChannelsLoading(true);
            const { data, metadata } = await getChannels({ page: currentPage, size: 25, search: search_string, signal: controller.signal });
            if (controller.signal.aborted) return;
            
            const newChannels = data.map(channel => ({
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar src={channel.platform_icon || channel.picture_url} size="small" style={{ flexShrink: 0 }}>
                            {(channel.channel_name || channel.name || channel.id || '?')[0].toUpperCase()}
                        </Avatar>
                        <span>{channel.channel_name || channel.name || channel.id}</span>
                    </div>
                ),
                value: channel.id,
                searchLabel: channel.channel_name || channel.name || channel.id,
            }));

            if (currentPage === 1) {
                setChannels(newChannels);
            } else {
                setChannels(prev => [...prev, ...newChannels]);
            }
            
            setHasMoreChannels(metadata.has_next);
            setChannelPage(metadata.page);
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || controller.signal.aborted) return;
            handleError(error);
        } finally {
            if (channelsAbortControllerRef.current === controller && !controller.signal.aborted) {
                setSearchChannelsLoading(false);
            }
        }
    }, [handleError]);

    const debouncedFetchChannels = useCallback(
        debounce((search) => {
            fetchChannels(search, 1);
        }, 500),
        [fetchChannels]
    );

    const fetchContacts = useCallback(async (search_string = '', currentPage = 1, explicitChannelId = null) => {
        if (contactsAbortControllerRef.current) {
            contactsAbortControllerRef.current.abort();
        }

        const controller = new AbortController();
        contactsAbortControllerRef.current = controller;

        try {
            const channelId = explicitChannelId || form.getFieldValue('channel_id');
            if (!channelId) {
                setContacts([]);
                return;
            }

            setSearchContactsLoading(true);
            const params = { page: currentPage, size: 25, search: search_string, filters: { channel_id: channelId }, signal: controller.signal };
            const { data, metadata } = await ContactsServices.getContacts(params);
            if (controller.signal.aborted) return;
            
            const newContacts = data.map(contact => ({
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar src={contact.picture_url} size="small" style={{ flexShrink: 0 }}>
                            {(contact.first_name || contact.last_name || contact.phone || contact.email || contact.id || '?')[0].toUpperCase()}
                        </Avatar>
                        <span>{`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.phone || contact.email || contact.id}</span>
                    </div>
                ),
                value: contact.id,
                searchLabel: `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.phone || contact.email || contact.id,
            }));

            if (currentPage === 1) {
                setContacts(newContacts);
            } else {
                setContacts(prev => [...prev, ...newContacts]);
            }
            
            setHasMore(metadata.has_next);
            setPage(metadata.page);
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || controller.signal.aborted) return;
            handleError(error);
        } finally {
            if (contactsAbortControllerRef.current === controller && !controller.signal.aborted) {
                setSearchContactsLoading(false);
            }
        }
    }, [form, handleError]);

    const debouncedFetchContacts = useCallback(
        debounce((search, channelId) => {
            fetchContacts(search, 1, channelId);
        }, 500),
        [fetchContacts]
    );

    useEffect(() => {
        fetchChannels('');
    }, [fetchChannels]);

    useEffect(() => () => {
        debouncedFetchChannels.cancel?.();
        debouncedFetchContacts.cancel?.();
        if (channelsAbortControllerRef.current) {
            channelsAbortControllerRef.current.abort();
        }
        if (contactsAbortControllerRef.current) {
            contactsAbortControllerRef.current.abort();
        }
    }, [debouncedFetchChannels, debouncedFetchContacts]);

    const handlePopupScroll = (e) => {
        const { target } = e;
        if (Math.ceil(target.scrollTop + target.offsetHeight) >= target.scrollHeight) {
            if (!searchContactsLoading && hasMore) {
                fetchContacts(searchStringContact, page + 1, selectedChannelId);
            }
        }
    };

    const handleChannelPopupScroll = (e) => {
        const { target } = e;
        if (Math.ceil(target.scrollTop + target.offsetHeight) >= target.scrollHeight) {
            if (!searchChannelsLoading && hasMoreChannels) {
                fetchChannels(searchStringChannel, channelPage + 1);
            }
        }
    };

    const handleFinish = async (values) => {
        setSubmitting(true);
        try {
            const payload = {
                subject: values.subject,
                category: values.category,
                priority: values.priority,
                description: values.description,
                contact_id: values.contact_id || undefined,
            };

            await onSubmit(payload, () => setSubmitting(false), form);
        } catch {
            setSubmitting(false);
        }
    };

    return (
        <ResponsiveModal
            open
            title={t('create_ticket')}
            onCancel={onClose}
            desktopWidth={600}
            footer={
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={onClose}>
                        {t('cancel')}
                    </Button>
                    <Button
                        type="primary"
                        loading={submitting}
                        onClick={() => form.submit()}
                    >
                        {t('create')}
                    </Button>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={{
                    category: 'other',
                    priority: 'medium',
                }}
            >
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="channel_id"
                            label={<span>{t('channel', 'Channel')} <Text type="danger">*</Text></span>}
                            rules={[{ required: true, message: t('channel_required', 'Channel is required') }]}
                        >
                            <Select
                                showSearch
                                placeholder={t('select_channel', 'Select a channel')}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.searchLabel ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        {searchChannelsLoading && channels.length > 0 && (
                                            <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                                <Spin size="small" />
                                            </div>
                                        )}
                                    </>
                                )}
                                options={channels}
                                onSearch={(value) => {
                                    setSearchStringChannel(value);
                                    debouncedFetchChannels(value);
                                }}
                                onChange={(val) => {
                                    form.setFieldValue('contact_id', undefined);
                                    if (val) {
                                        fetchContacts('', 1, val);
                                    } else {
                                        setContacts([]);
                                    }
                                }}
                                onPopupScroll={handleChannelPopupScroll}
                                loading={searchChannelsLoading}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="contact_id"
                            label={<span>{t('contact', 'Contact')} <Text type="danger">*</Text></span>}
                            rules={[{ required: true, message: t('contact_required', 'Contact is required') }]}
                        >
                            <Select
                                showSearch
                                placeholder={t('select_contact', 'Select a contact')}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.searchLabel ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        {searchContactsLoading && contacts.length > 0 && (
                                            <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                                <Spin size="small" />
                                            </div>
                                        )}
                                    </>
                                )}
                                options={contacts}
                                onSearch={(value) => {
                                    setSearchStringContact(value);
                                    debouncedFetchContacts(value, form.getFieldValue('channel_id'));
                                }}
                                onPopupScroll={handlePopupScroll}
                                loading={searchContactsLoading}
                                allowClear
                                disabled={!selectedChannelId}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="subject"
                    label={<span>{t('ticket_subject')} <Text type="danger">*</Text></span>}
                    rules={[{ required: true, message: t('subject_required') }]}
                >
                    <Input placeholder={t('enter_ticket_subject')} maxLength={255} />
                </Form.Item>

                <Form.Item
                    name="description"
                    label={t('ticket_description')}
                >
                    <TextArea
                        rows={4}
                        placeholder={t('enter_ticket_description')}
                        maxLength={5000}
                        showCount
                        dir="auto"
                        style={{ unicodeBidi: 'plaintext' }}
                    />
                </Form.Item>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="category"
                            label={t('ticket_category')}
                        >
                            <Select>
                                {CATEGORIES.map(cat => (
                                    <Select.Option key={cat} value={cat}>
                                        {t(`ticket_category_${cat}`) || cat}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="priority"
                            label={t('ticket_priority')}
                        >
                            <Select>
                                {PRIORITIES.map(pri => (
                                    <Select.Option key={pri} value={pri}>
                                        {t(`ticket_priority_${pri}`) || pri}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </ResponsiveModal>
    );
};

export default CreateTicketModal;
